import useHashScroll from 'hooks/useHashScroll';
import { useState, useEffect, useRef } from 'react'; // Added
import useWindowDimensions from './useWindowDimensions';
const useScrollableTabs = ({ children = [], onTabSelect = null }) => {
    const [activeTab, setActiveTab] = useState('tab1');
    const screenHeight = window.innerHeight;
    const { width } = useWindowDimensions();
    const { scrollToHashElement } = useHashScroll(
        50,
        width > 1280 ? 58 : 110, // offset from the top
        'instant'
    );
    const indicatorRef = useRef(null); // Added ref for the indicator
    const elementRef = useRef(null);
    const tabContentRef = useRef(null);
    const [tabsLoaded, setTabsLoaded] = useState([]);
    const [tabsVisibleOnScreen, setTabsVisibleOnScreen] = useState([]);
    // differentiate from scroll behaviour for tab switch
    const [tabClicked, setTabClicked] = useState(false);

    const switchTab = (tabref, isClickedTab) => {
        setActiveTab(tabref);
        if (onTabSelect) onTabSelect(tabref);

        // making it false after switching the tab
        if (isClickedTab) setTimeout(() => setTabClicked(false), 100);
    };

    // get visible tabs (number of tabs currently visible on the screen)
    const getVisibleTabContentOnScreen = () => {
        if (!tabContentRef?.current) return [];
        const tabElements = tabContentRef.current.querySelectorAll(
            '.tab-content-wrapper'
        );

        // Calculate the number of tabs currently visible on the screen
        const visibleTabs = Array.from(tabElements)
            .filter((tabElement) => {
                const rect = tabElement.getBoundingClientRect();
                return rect.top >= 0 && rect.bottom <= window.innerHeight;
            })
            ?.map((tab) => tab.dataset.tabref);

        return visibleTabs;
    };

    useEffect(() => {
        const handleScroll = () => {
            // Check each tab's position and update the active tab accordingly
            children.forEach((element) => {
                if (element) {
                    const { tabref, id } = element.props;
                    const tabElement = document.getElementById(id);
                    if (tabElement) {
                        const tabTop = tabElement.getBoundingClientRect().top;
                        // Check if the top of the tab content is within the viewport
                        if (tabTop <= screenHeight - 200 && !tabClicked) {
                            switchTab(tabref);
                        }
                    }
                }
            });

            const visibleTabs = getVisibleTabContentOnScreen();
            if (visibleTabs.length > 0 && JSON.stringify(visibleTabs) !== JSON.stringify(tabsVisibleOnScreen))
                setTabsVisibleOnScreen(visibleTabs);
        };

        // Attach the scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [tabClicked]);

    useEffect(() => {
        // Adjust the scroll position of the elementRef when the active tab changes
        const element = elementRef.current;
        if (element) {
            const tabRefElement = indicatorRef?.current;
            if (tabRefElement && width < 1280) {
                // Scroll horizontally
                const scrollLeft = tabRefElement?.offsetLeft - 50; // Adjust as needed
                element.scrollLeft = scrollLeft;
            } else {
                // Scroll vertically on tab switch
                const tabTopOffset = tabRefElement?.offsetTop - 20; // Adjust as needed
                element.scrollTop = tabTopOffset;
            }
        }
    }, [activeTab]);

    return {
        activeTab,
        indicatorRef,
        elementRef,
        tabContentRef,
        tabCached: tabsLoaded,
        tabsVisibleOnScreen,
        switchTab,
        scrollToHashElement,
        setTabsLoaded,
        setTabClicked,
    };
};

export default useScrollableTabs;
